/**
 * The pieces every view is dressed with, so the app reads as one thing.
 *
 * They all stand on `.card-art` (index.css): a tone, a wash of it, a motif,
 * and an icon well. Each view has a tone of its own — Chat is teal, Research
 * violet, Team Work blue, and so on (`VIEW_TONES`) — which shows up in its
 * hero, its empty states and its numbers, and is what lets a person know
 * where they are from the corner of their eye. The pieces:
 *
 *   ArtHero        what a view says before there is anything in it
 *   ArtSuggestion  a thing to try, as a card that is a button
 *   ArtStat        one number, with its label and a line of detail
 *   ArtEmpty       "nothing here yet", with what to do about it
 *   ViewHeader     a view's title band: icon well, title, blurb, actions
 *   ArtSection     a titled card for one part of a view: small icon well, title, count, actions
 *
 * Artwork is the piece's own icon, large and faint, behind the text — a
 * picture of what the thing is rather than decoration. Colour is never the
 * only signal: every state also has words or a shape.
 */
import { cloneElement, isValidElement, type ReactNode } from 'react';

import { FitText } from './FitText.tsx';

export type ArtTone = 'teal' | 'violet' | 'amber' | 'blue' | 'rose' | 'green' | 'slate';
export type ArtMotif = 'rings' | 'arc' | 'dots';

/** One tone per view, used wherever that view shows a hero, an empty state or a number. */
export const VIEW_TONES: Record<string, ArtTone> = {
  home: 'teal', // where the console opens: the primary place
  workbenches: 'teal', // the fleet: the primary place
  nodes: 'amber', // shared compute, and the approvals it waits on
  activity: 'blue', // oversight
  portfolio: 'violet', // the organisation's shape: areas, their projects, their teams
  inbox: 'amber', // what waits on a person
  memory: 'violet', // what the organisation knows
  budgets: 'amber', // money that waits on someone
  costs: 'green', // the ledger: what the work cost, written down
  audit: 'slate', // the record: calm, evidentiary
  labelling: 'violet', // what the organisation knows, and how it is marked: memory's family, away from the audit's slate
  teams: 'violet', // the agents themselves: next to Portfolio's shape, thinking and knowledge
  projects: 'blue', // coordination: who works where
  policies: 'rose', // structure and change
  assurance: 'slate', // the register of what providers do with data: calm, evidentiary, like the audit
  catalogue: 'green', // things written down
  customisations: 'blue', // what every machine is set up with: oversight, between the catalogue's green and secrets' slate
  'apps-and-routines': 'amber', // what people share for a project and what waits on its owners: things scheduled or waiting, as routines are in the workbench
  foundry: 'blue', // where people may work with Microsoft Foundry and who answers for it: coordination and oversight, between amber and violet neighbours
  compute: 'rose', // the clusters work runs on: structure, between foundry's blue and design systems' violet
  'design-systems': 'violet', // the organisation's look: identity, between customisations' blue and secrets' slate
  tools: 'teal', // what agents work with: the primary family, away from its neighbours' tones
  secrets: 'slate', // kept calm, like settings: names and facts, no artwork fuss
  mesh: 'violet', // the network between everything
  enrol: 'amber', // waiting on a person
  team: 'violet', // Mojo Up AI Cloud: the people in an organisation
  quotas: 'amber', // limits: how much may be kept, and what waits at the edge of it
  billing: 'green', // Mojo Up AI Cloud: the plan, its usage and its invoices: money written down, as Costs is
  backups: 'green', // what is kept: written down, sealed, there to come back to
  updates: 'rose', // change: what the fleet runs and what it moves to
  downloads: 'teal', // the primary place to start: the app itself
  settings: 'slate',
};

function artOf(icon: ReactNode, className: string): ReactNode {
  return isValidElement<{ className?: string }>(icon) ? cloneElement(icon, { className }) : null;
}

/** What a view says before there is anything in it: who you are talking to, and what to try. */
export function ArtHero({ tone, icon, title, blurb, children }: { tone: ArtTone; icon: ReactNode; title: ReactNode; blurb?: ReactNode; children?: ReactNode }) {
  return (
    <div className="art-hero" data-tone={tone} data-art-hero>
      <span className="art-hero__glow" aria-hidden="true" />
      <span className="card-art__icon art-hero__icon">{icon}</span>
      <h2 className="art-hero__title">{title}</h2>
      {blurb ? <p className="art-hero__blurb">{blurb}</p> : null}
      {children ? <div className="art-hero__body">{children}</div> : null}
    </div>
  );
}

/** A thing to try: a card that is a button, wearing its own icon as artwork. */
export function ArtSuggestion({ tone, motif = 'arc', icon, children, onPress, disabled }: { tone: ArtTone; motif?: ArtMotif; icon?: ReactNode; children: ReactNode; onPress: () => void; disabled?: boolean }) {
  return (
    <button type="button" className="card-art art-suggestion" data-tone={tone} data-motif={motif} data-pressable={disabled ? undefined : true} disabled={disabled} onClick={onPress}>
      {icon ? (
        <span className="art-backdrop" aria-hidden="true">
          {artOf(icon, 'art-backdrop__icon')}
        </span>
      ) : null}
      {icon ? <span className="card-art__icon art-suggestion__icon">{icon}</span> : null}
      <span className="art-suggestion__text">{children}</span>
    </button>
  );
}

/** One number. `needsYou` is the single accent a row of these may carry: something waits on a person. */
export function ArtStat({
  tone,
  motif = 'rings',
  icon,
  label,
  value,
  detail,
  needsYou,
  selected,
  onPress,
}: {
  tone: ArtTone;
  motif?: ArtMotif;
  icon?: ReactNode;
  label: string;
  /** Already formatted. `undefined` or an unreadable number shows as a dash, never as "NaN". */
  value: ReactNode;
  detail?: ReactNode;
  needsYou?: boolean;
  /** A pressable card used as a filter: shown as on, and announced as pressed. */
  selected?: boolean;
  onPress?: () => void;
}) {
  const shown = value === undefined || value === null || value === '' || (typeof value === 'number' && !Number.isFinite(value)) ? '—' : value;
  const body = (
    <>
      <span className="art-stat__head">
        {icon ? <span className="card-art__icon art-stat__icon">{icon}</span> : null}
        <FitText className="art-stat__label">{label}</FitText>
      </span>
      <span className="art-stat__value">{shown}</span>
      {/* Always a line, so a row of these keeps its numbers on one baseline. */}
      <FitText className={`art-stat__detail ${needsYou ? 'art-stat__detail--needs-you' : ''}`}>{detail ?? ''}</FitText>
    </>
  );
  return onPress ? (
    <button type="button" className="card-art art-stat" data-tone={tone} data-motif={motif} data-pressable {...(selected !== undefined ? { 'aria-pressed': selected } : {})} data-selected={selected || undefined} onClick={onPress}>
      {body}
    </button>
  ) : (
    <div className="card-art art-stat" data-tone={tone} data-motif={motif}>
      {body}
    </div>
  );
}

/** A number for `ArtStat`: grouped digits, or a dash when there is nothing to show. */
export function statNumber(n: number | undefined | null): string {
  return typeof n === 'number' && Number.isFinite(n) ? n.toLocaleString() : '—';
}

/** "Nothing here yet" — what it is, and what to do about it. `compact` is for a column or a side pane. */
export function ArtEmpty({ tone, icon, title, children, action, compact }: { tone: ArtTone; icon: ReactNode; title: ReactNode; children?: ReactNode; action?: ReactNode; compact?: boolean }) {
  return (
    <div className="card-art art-empty" data-tone={tone} data-motif="dots" data-compact={compact || undefined} data-art-empty>
      <span className="art-backdrop art-backdrop--centre" aria-hidden="true">
        {artOf(icon, 'art-backdrop__icon')}
      </span>
      <span className="card-art__icon art-empty__icon">{icon}</span>
      <span className="art-empty__title">{title}</span>
      {children ? <span className="art-empty__text">{children}</span> : null}
      {action ? <span className="art-empty__action">{action}</span> : null}
    </div>
  );
}

/** A view's title band. The icon well carries the view's tone; actions sit at the far end. */
export function ViewHeader({ tone, icon, title, blurb, actions, children }: { tone: ArtTone; icon: ReactNode; title: ReactNode; blurb?: ReactNode; actions?: ReactNode; children?: ReactNode }) {
  return (
    <header className="view-header" data-tone={tone}>
      <span className="card-art__icon view-header__icon">{icon}</span>
      <div className="view-header__text">
        <h1 className="view-header__title">{title}</h1>
        {blurb ? <div className="view-header__blurb">{blurb}</div> : null}
      </div>
      {children}
      {actions ? <div className="view-header__actions">{actions}</div> : null}
    </header>
  );
}

/**
 * One part of a view: a card with a small icon well, a title, an optional
 * count and actions, then whatever it holds. `flush` drops the body padding
 * for content that brings its own edges — a canvas, a table, a list of rows.
 */
export function ArtSection({
  tone,
  icon,
  title,
  count,
  hint,
  actions,
  children,
  flush,
  className,
}: {
  tone: ArtTone;
  icon: ReactNode;
  title: ReactNode;
  count?: ReactNode;
  /** A short line after the title: a legend, a caveat. */
  hint?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  flush?: boolean;
  className?: string;
}) {
  return (
    <section className={`art-section ${className ?? ''}`} data-tone={tone}>
      <header className="art-section__head">
        <span className="card-art__icon art-section__icon">{icon}</span>
        <h2 className="art-section__title">{title}</h2>
        {count !== undefined && count !== null && count !== '' ? <span className="art-section__count">{count}</span> : null}
        {hint ? <span className="art-section__hint">{hint}</span> : null}
        {actions ? <span className="art-section__actions">{actions}</span> : null}
      </header>
      <div className={flush ? 'art-section__body art-section__body--flush' : 'art-section__body'}>{children}</div>
    </section>
  );
}
